import type { PortfolioBreakdownHierarchyNode } from 'venn-components';
import { ColorUtils } from 'venn-ui-kit';

export type ColorMap = Map<string, string>;
export type ColorStrategy = (params: { parentColor: string; childrenCount: number; childIndex: number }) => string;

/**
 * Generates a color map for a portfolio breakdown tree using the provided strategy
 * @param nodes - Array of portfolio breakdown hierarchy nodes
 * @param colorStrategy - Function that generates color based on parent color, siblings count and item index
 * @param parentColor - Color inherited from the parent node (required for root level)
 * @param colorMap - Existing color map to populate (mutated in place)
 * @returns The updated color map containing node IDs mapped to their assigned colors
 */
export function fillColorMap(
  nodes: PortfolioBreakdownHierarchyNode[],
  colorStrategy: ColorStrategy,
  parentColor: string,
  colorMap: ColorMap = new Map(),
): ColorMap {
  nodes.forEach((node, index) => {
    // Skip if already colored (this allows pre-setting specific node colors)
    if (colorMap.has(node.id)) {
      if (node.children?.length) {
        fillColorMap(node.children, colorStrategy, colorMap.get(node.id)!, colorMap);
      }
      return;
    }

    const color = colorStrategy({
      parentColor,
      childrenCount: nodes.length,
      childIndex: index,
    });

    colorMap.set(node.id, color);

    if (node.children?.length) {
      fillColorMap(node.children, colorStrategy, color, colorMap);
    }
  });

  return colorMap;
}

export const shadingStrategy: ColorStrategy = ({ parentColor, childrenCount, childIndex }) => {
  const colorShadeRatio = 1 / (childrenCount + 1);
  return ColorUtils.opacify(parentColor, colorShadeRatio * (childrenCount - childIndex));
};
